// Shapes the latency-vs-load response into one chart row per load band with a
// column per month. Types mirror src/server/queries/api-latency-vs-load.ts.
export interface LatencyLoadPoint {
  month: string;
  route: string | null;
  band: number;
  requests: number;
  p95Ms: number;
}

export interface LatencyVsLoad {
  from: string;
  points: LatencyLoadPoint[];
}

/**
 * A p95 needs enough requests behind it to mean anything: below this a point
 * is one or two slow requests, so the chart leaves a gap instead.
 */
export const MIN_REQUESTS_PER_POINT = 20;

export interface BandRow {
  band: number;
  label: string;
  /** p95 ms per month (null = too few requests), and `${month}:n` = requests behind it. */
  [month: string]: number | string | null;
}

export const bandLabel = (band: number) => `${2 ** band}–${2 ** (band + 1) - 1}`;

/** Endpoints by request count over the window, busiest first. */
export function routesByVolume(points: LatencyLoadPoint[]): string[] {
  const totals = new Map<string, number>();
  for (const p of points) {
    if (p.route !== null) totals.set(p.route, (totals.get(p.route) ?? 0) + p.requests);
  }
  return [...totals].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([r]) => r);
}

/** Every month in the window (from the all-endpoints rows), oldest first. */
export function monthsOf(points: LatencyLoadPoint[]): string[] {
  return [...new Set(points.filter((p) => p.route === null).map((p) => p.month))].sort();
}

export function bandRows(points: LatencyLoadPoint[], route: string | null): BandRow[] {
  const rows = new Map<number, BandRow>();
  for (const p of points) {
    if (p.route !== route) continue;
    let row = rows.get(p.band);
    if (!row) rows.set(p.band, (row = { band: p.band, label: bandLabel(p.band) }));
    row[p.month] = p.requests >= MIN_REQUESTS_PER_POINT ? p.p95Ms : null;
    row[`${p.month}:n`] = p.requests;
  }
  return [...rows.values()].sort((a, b) => a.band - b.band);
}
